import { ArrowUpRight, ChevronDown, X, Search, ChevronsUpDown, ArrowUpDown, Star } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton, setSelectedAgent } from '../../store/reducers/index';
import { useState, useEffect, useMemo } from 'react';
import { useLanguage, getReviewAgentsTranslation, getReviewSenderDetailsTranslation } from '../../hooks/useLanguage';
import AiIcon from '../../assets/ai-icon.png';

export const ReviewAgents = ({ agents, navigate, userData, declarationIndex = 0 }) => {
    const dispatch = useDispatch();
    const selectedSenders = useSelector(state => state.user.selectedSender || []);
    const selectedReceivers = useSelector(state => state.user.selectedReceiver || []);
    const selectedAgents = useSelector(state => state.user.selectedAgent || []);
    const selectedSender = selectedSenders[declarationIndex] || null;
    const selectedReceiver = selectedReceivers[declarationIndex] || null;
    const selectedAgent = selectedAgents[declarationIndex] || null;
    const suggestions = useSelector(state => {
      const suggestionsState = state.suggestions;
      const suggestionsArray = suggestionsState?.suggestions;
      
      return Array.isArray(suggestionsArray) ? suggestionsArray : [];
    });
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name-asc');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isSpanish] = useLanguage();

    // Get suggestions for current declaration index
    // Validate suggestions array before using find
    const currentSuggestions = Array.isArray(suggestions) && suggestions.length > 0
      ? suggestions.find(s => s !== null && s !== undefined && typeof s === 'object' && s.exportacion_index === declarationIndex)
      : null;
    const suggestedAgents = (currentSuggestions?.agents && Array.isArray(currentSuggestions.agents)) 
      ? currentSuggestions.agents 
      : [];
    

    // Match suggested agents to contacts by id, nif, or name
    const matchedSuggestedAgents = suggestedAgents
        .map(suggestion => {
            const matchedContact = agents.find(agent => 
                agent.id === suggestion.id ||
                (agent.nif && suggestion.nif && agent.nif === suggestion.nif) ||
                (agent.nombre && suggestion.nombre && agent.nombre.toLowerCase() === suggestion.nombre.toLowerCase())
            );
            return matchedContact ? { ...matchedContact, score: suggestion.score, reason: suggestion.reason } : null;
        })
        .filter(Boolean)
        .sort((a, b) => (b.score || 0) - (a.score || 0)) // Sort by score descending
        .slice(0, 3); // Limit to top 3

    const sortOptions = [
        { value: 'name-asc', label: getReviewAgentsTranslation('nameAZ', isSpanish) },
        { value: 'name-desc', label: getReviewAgentsTranslation('nameZA', isSpanish) },
        { value: 'date-desc', label: getReviewAgentsTranslation('newestFirst', isSpanish) },
        { value: 'date-asc', label: getReviewAgentsTranslation('oldestFirst', isSpanish) }
    ];

    const handleAgentSelect = (agent) => {
        dispatch(setSelectedAgent(agent, declarationIndex));
        setIsModalOpen(false);
        setSearchQuery('');
    };

    // Filter agents by search query across multiple fields
    const filteredAgents = useMemo(() => {
        if (!searchQuery || typeof searchQuery !== 'string') {
            return agents;
        }
        
        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return agents;
        }
        
        return agents.filter(agent => {
            if (!agent || typeof agent !== 'object') {
                return false;
            }
            
            // Helper function to safely convert and search a field
            const searchInField = (field) => {
                if (field == null) return false;
                if (typeof field === 'string' && field.trim() === '') return false;
                if (typeof field === 'number') {
                    return String(field).toLowerCase().includes(query);
                }
                const fieldStr = String(field).toLowerCase().trim();
                return fieldStr.length > 0 && fieldStr.includes(query);
            };
            
            // Search across multiple fields
            return searchInField(agent.nombre) ||
                   searchInField(agent.identificacion_fiscal) ||
                   searchInField(agent.nif) ||
                   searchInField(agent.eori) ||
                   searchInField(agent.direccion) ||
                   searchInField(agent.localidad) ||
                   searchInField(agent.pais);
        });
    }, [agents, searchQuery]);

    // Sort filtered agents
    const sortedAgents = useMemo(() => {
        return [...filteredAgents].sort((a, b) => {
            switch (sortBy) {
                case 'name-asc':
                    return (a.nombre || '').localeCompare(b.nombre || '');
                case 'name-desc':
                    return (b.nombre || '').localeCompare(a.nombre || '');
                case 'date-asc':
                    return new Date(a.date_created || 0) - new Date(b.date_created || 0);
                case 'date-desc':
                    return new Date(b.date_created || 0) - new Date(a.date_created || 0);
                default:
                    return 0;
            }
        });
    }, [filteredAgents, sortBy]);

    const getSortLabel = () => {
        return sortOptions.find(option => option.value === sortBy)?.label || getReviewAgentsTranslation('sortBy', isSpanish);
    };

    return (
        <div className="text-left text-sm flex flex-col" id='review-agents'>
            <div className='flex justify-between gap-4 mb-2 items-center'>
                <h1 className='font-semibold'>{getReviewAgentsTranslation('agent', isSpanish)} <span className='text-sm font-normal italic'>{getReviewAgentsTranslation('optional', isSpanish)}</span></h1>
            </div>

            <div className={`p-4 border-container rounded-xl form ${selectedAgent && "flex-1"}`}>
                <div className='relative w-full'>
                    <button 
                        className={`w-full flex gap-2 justify-between items-center ${selectedAgent ? "border-container rounded-md p-4 " : ""}`} 
                        onClick={() => setIsModalOpen(true)}
                    >
                        {selectedAgent ? <p className='truncate'>{selectedAgent.nombre}</p> : <p className='opacity-75 truncate'>{getReviewAgentsTranslation('selectAgent', isSpanish)}</p>}
                        <ChevronsUpDown className="w-4 h-4" />
                    </button>
                </div>
                {selectedAgent && (
                    <>
                        <div className='review-flex flex gap-4 mt-4'>
                            <div className="form-group flex-1">
                                <label htmlFor="companyName">{getReviewAgentsTranslation('agentName', isSpanish)}</label>
                                <p className='font-bold'>{selectedAgent && selectedAgent.nombre || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1">
                                <label htmlFor="address">{getReviewAgentsTranslation('agentTaxId', isSpanish)}</label>
                                <p className='font-bold'>{selectedAgent && selectedAgent.nif || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1">
                                <label htmlFor="taxId">{getReviewAgentsTranslation('agentEori', isSpanish)}</label>
                                <p className='font-bold'>{selectedAgent && selectedAgent.eori || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1">
                                <label htmlFor="eori">{getReviewAgentsTranslation('agentAddress', isSpanish)}</label>
                                <p className='font-bold'>{selectedAgent && selectedAgent.direccion || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1">
                                <label htmlFor="city">{getReviewAgentsTranslation('agentCity', isSpanish)}</label>
                                <p className='font-bold'>{selectedAgent && selectedAgent.localidad || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1">
                                <label htmlFor="postalCode">{getReviewAgentsTranslation('agentCountry', isSpanish)}</label>
                                <p className='font-bold'>{selectedAgent && selectedAgent.pais || "N/A"}</p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Suggested Agents */}
            {!selectedAgent && matchedSuggestedAgents.length > 0 && (
                <>
                <h2 className="text-xs font-light mt-4">{getReviewAgentsTranslation('suggestedAgents', isSpanish)}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                    {matchedSuggestedAgents.map((suggestedAgent, index) => (
                        <button
                            key={`suggested-agent-${declarationIndex}-${suggestedAgent.id || index}-${index}`}
                            onClick={() => handleAgentSelect(suggestedAgent)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full transition-colors"
                            title={suggestedAgent.reason || `Score: ${(suggestedAgent.score * 100).toFixed(0)}%`}
                        >
                            <img src={AiIcon} alt="AI Icon" className="w-4 h-4" />
                            <span className="font-medium text-sm truncate">{suggestedAgent.nombre}</span>
                            {suggestedAgent.score && (
                                <span className="text-[8px] text-blue-400 font-semibold ">
                                    {(suggestedAgent.score * 100).toFixed(0)}%
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                </>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{getReviewAgentsTranslation('selectAgent', isSpanish)}</h2>
                            <button onClick={() => {
                                setIsModalOpen(false);
                                setSearchQuery('');
                            }} className="p-1 hover:bg-gray-100 rounded-full">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="flex gap-2 mb-4">
                            {/* Search Input */}
                            <div className="relative flex-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border-container rounded-full"
                                    placeholder={getReviewAgentsTranslation('searchAgents', isSpanish)}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            {/* Custom Sort Dropdown */}
                            <div className="relative button-no-hover-effect">
                                <button
                                    className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                                    onClick={() => setIsSortOpen(!isSortOpen)}
                                >
                                    <ArrowUpDown className="w-4 h-4" />
                                    <span>{getSortLabel()}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {isSortOpen && (
                                    <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                                        {sortOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                    sortBy === option.value ? 'text-primary' : 'text-gray-700'
                                                }`}
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setIsSortOpen(false);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
                            {agents.length > 0 ? (
                                <div className="space-y-0">
                                    {sortedAgents.length > 0 ? (
                                        sortedAgents.map((agent, index) => (
                                            <div 
                                                key={`agent-${declarationIndex}-${agent.id || index}-${index}`} 
                                                className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray'
                                                onClick={() => handleAgentSelect(agent)}
                                            >
                                                <div className="flex justify-between items-start gap-2">
                                                    <div>
                                                        <p className="font-medium">{agent.nombre}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {agent.pais}
                                                        </p>
                                                    </div>
                                                    {agent.nif && (
                                                        <span className="text-xs bg-bgGray px-3 py-1 rounded-full">
                                                            {agent.nif}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            {getReviewAgentsTranslation('noAgentsFound', isSpanish)} "{searchQuery}"
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className='opacity-75 mb-4'>{getReviewAgentsTranslation('noAgentsAvailable', isSpanish)}</p>
                                    <button 
                                        className='flex gap-2 items-center justify-center mx-auto px-4 py-2 bg-default text-white rounded-full'
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            dispatch(setShowBackToDashboardButton(true));
                                            navigate("/customs-agent");
                                        }}
                                    >
                                        <p className='text-white'>{getReviewAgentsTranslation('addAgent', isSpanish)}</p>
                                        <ArrowUpRight className='w-5 h-5 stroke-white' />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}